import { Boxes, ChevronLeft, CircleAlert, Clock, Download, LayoutGrid, LoaderCircle, Lock, MapPin, Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { SCHEMATIC_EXTENSIONS } from "@mcp/world";
import { controller } from "../../app/controller.ts";
import { schematicExt, type SchematicEntry } from "../../lib/schematics.ts";
import { useApp } from "../../state/store.ts";
import { AcademyLink } from "../Academy.tsx";
import { FileDetails, ImportForm, MissingDetails, type Missing } from "./SchematicDetails.tsx";
import { loadModel, StagePane } from "./StagePane.tsx";
import type { SchematicStage } from "./stage.ts";
import "./schematics.css";

const ACCEPT = SCHEMATIC_EXTENSIONS.join(",");
/** what the import accepts (a .zip / .mcstructure gets a readable "not a schematic" message from the reader) */
const isSchematicFile = (f: File) => SCHEMATIC_EXTENSIONS.some((x) => f.name.toLowerCase().endsWith(x)) || /\.(mcstructure|zip)$/i.test(f.name);
const RECENT_MS = 7 * 86400_000;
const THUMB_VERSION = 1;

type Filter = "all" | "placed" | "missing" | "recent" | ".litematic" | ".schem" | ".schematic" | ".nbt";

async function importFiles(files: File[]) {
  const ok = files.filter(isSchematicFile);
  if (!ok.length && files.length) { useApp.getState().set({ error: "That's not a schematic file (.litematic, .schem, .schematic or .nbt)." }); return; }
  for (const f of ok) await controller.importAndEdit(f);
}

/**
 * Schematics in a full window (key 7, the tool dock, schematic rows in the plan list): library rail, the selected
 * build in 3D, a filmstrip of all of them, details with materials and "Place in ‹world›". Files stay in this
 * browser (lib/schematics.ts); the plan only keeps hash, size, orientation, materials and the source link.
 * Mounted once at the top level; renders nothing unless open (layoutPanel === "schematics").
 */
export function SchematicsView() {
  const open = useApp((s) => s.layoutPanel === "schematics" && s.phase === "ready");
  // one window, wherever it's mounted from: the first mounted instance draws it, straight into <body> (a fixed
  // overlay must not be trapped by a parent with a transform or backdrop-filter)
  const [me] = useState(() => ({}));
  const [mine, setMine] = useState(false);
  useEffect(() => {
    if (owner) return;
    owner = me;
    setMine(true);
    return () => { if (owner === me) owner = null; };
  }, []);
  return open && mine ? createPortal(<SchematicsWindow />, document.body) : null;
}
let owner: object | null = null;

function SchematicsWindow() {
  const list = useApp((s) => s.schematics);
  const plans = useApp((s) => s.plans);
  const world = useApp((s) => s.level?.name) || "this world";
  const selected = useApp((s) => s.schemSelected);
  const editHash = useApp((s) => s.schemEdit);
  const importing = useApp((s) => s.schemImporting);
  const assetsLabel = useApp((s) => (s.mapColoursOnly ? "map colours" : s.assetsLabel ?? "textures"));
  const [filter, setFilter] = useState<Filter>("all");
  const [q, setQ] = useState("");
  const [turns, setTurns] = useState(0);
  const [mirror, setMirror] = useState(false);
  const [top, setTop] = useState(false);
  const [drag, setDrag] = useState(false);
  const [stage, setStage] = useState<SchematicStage | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const search = useRef<HTMLInputElement>(null);

  // ---- what's where ----
  const placedCount = useMemo(() => {
    const m = new Map<string, number>();
    for (const o of plans) if (o.schematic) m.set(o.schematic.hash, (m.get(o.schematic.hash) ?? 0) + 1);
    return m;
  }, [plans]);
  const missing: Missing[] = useMemo(() => controller.missingSchematics(), [plans, list]);
  const now = Date.now();
  const counts = {
    all: list.length,
    placed: list.filter((e) => placedCount.has(e.hash)).length,
    missing: missing.length,
    recent: list.filter((e) => now - e.addedAt < RECENT_MS).length,
  };
  const types = (SCHEMATIC_EXTENSIONS as string[]).map((x) => [x, list.filter((e) => schematicExt(e.fileName) === x).length] as const).filter(([, n]) => n > 0);

  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const hit = (...s: (string | undefined)[]) => words.every((w) => s.some((x) => x?.toLowerCase().includes(w)));
  const files = useMemo(() => list
    .filter((e) => filter === "all" || (filter === "placed" ? placedCount.has(e.hash) : filter === "recent" ? now - e.addedAt < RECENT_MS : filter === "missing" ? false : schematicExt(e.fileName) === filter))
    .filter((e) => hit(e.name, e.fileName, e.author, e.sourceUrl))
    .sort((a, b) => (b.usedAt ?? b.addedAt) - (a.usedAt ?? a.addedAt)), [list, filter, q, placedCount]);
  const gone = filter === "all" || filter === "missing" || filter === "placed" ? missing.filter((m) => hit(m.name, m.ref.file, m.sourceUrl)) : [];

  // ---- selection: the chosen one if it's shown, else the first ----
  const visible = [...files.map((e) => e.hash), ...gone.map((m) => m.ref.hash)];
  const sel = selected && visible.includes(selected) ? selected : visible[0] ?? null;
  const entry = files.find((e) => e.hash === sel) ?? null;
  const miss = entry ? null : gone.find((m) => m.ref.hash === sel) ?? null;
  const select = (hash: string) => useApp.getState().set({ schemSelected: hash, ...(useApp.getState().schemEdit && useApp.getState().schemEdit !== hash ? { schemEdit: null } : {}) });
  useEffect(() => { setTurns(0); setMirror(false); }, [sel]);
  const editing = editHash ? list.find((e) => e.hash === editHash) ?? null : null;

  // ---- keys: Esc back to the map, / search, R turn, F mirror, ←/→ through the filmstrip ----
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing = !!t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
      if (document.querySelector(".modal-back")) return; // a dialog over the view owns its keys
      if (typing) {
        if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); if (t === search.current && q) setQ(""); else t!.blur(); }
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.code === "KeyF") { e.preventDefault(); e.stopPropagation(); search.current?.focus(); return; }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      let used = true;
      if (e.key === "Escape") { if (useApp.getState().schemEdit) useApp.getState().set({ schemEdit: null }); else controller.closeSchematics(); }
      else if (e.key === "/") search.current?.focus();
      else if (e.code === "KeyR" && entry) setTurns((v) => (v + (e.shiftKey ? 3 : 1)) % 4);
      else if (e.code === "KeyF" && entry) setMirror((v) => !v);
      else if ((e.key === "ArrowRight" || e.key === "ArrowLeft") && visible.length) {
        const i = Math.max(0, visible.indexOf(sel ?? ""));
        select(visible[(i + (e.key === "ArrowRight" ? 1 : visible.length - 1)) % visible.length]);
      } else if (e.code === "Digit7" || e.code === "Numpad7") controller.closeSchematics();
      else used = e.key !== "?" && e.key !== "F1" && e.key !== "Tab" && e.key !== "Enter" && e.key !== " ";
      if (used) { e.preventDefault(); e.stopPropagation(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  });

  // ---- filmstrip thumbnails: rendered here, one at a time, with the person's own textures ----
  const thumbKey = (e: SchematicEntry) => `${assetsLabel}|${THUMB_VERSION}|${e.size.join("x")}`;
  const running = useRef(false);
  const failed = useRef(new Set<string>());
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!stage || running.current) return;
    const next = list.find((e) => e.thumbKey !== thumbKey(e) && !failed.current.has(e.hash));
    if (!next) return;
    // after the selected one's preview had its turn in the workers
    const t = setTimeout(() => {
      running.current = true;
      void loadModel(stage, next, true)
        .then((m) => {
          if (!stage.alive) { m.dispose(); return; }
          const url = stage.thumbnail(m);
          if (url) return controller.setSchematicThumb(next.hash, url, thumbKey(next));
          failed.current.add(next.hash);
        })
        .catch(() => { failed.current.add(next.hash); })
        .finally(() => { running.current = false; setTick((v) => v + 1); });
    }, 400);
    return () => clearTimeout(t);
  });

  const pick = () => fileInput.current?.click();
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDrag(false);
    void importFiles([...e.dataTransfer.files]);
  };

  const empty = !list.length && !missing.length;
  const railItem = (id: Filter, icon: React.ReactNode, label: string, n: number, amber = false) => (
    <button type="button" className="sv-rail-item" aria-current={filter === id ? "true" : undefined} onClick={() => setFilter(id)}>
      {icon}<span className="grow">{label}</span>{amber && n ? <span className="sv-amber-count">{n}</span> : <span className="sv-count tabular">{n}</span>}
    </button>
  );
  const filterName = filter === "all" ? "All schematics" : filter === "placed" ? `Placed in ${world}` : filter === "missing" ? "Not on this computer" : filter === "recent" ? "Recently imported" : `${filter} files`;

  return (
    <div
      className={`sv${drag ? " dragging" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label="Schematics"
      onDragOver={(e) => { if (e.dataTransfer.types.includes("Files")) { e.preventDefault(); setDrag(true); } }}
      onDragLeave={(e) => { if (e.currentTarget === e.target || !e.currentTarget.contains(e.relatedTarget as Node)) setDrag(false); }}
      onDrop={onDrop}
    >
      <header className="sv-bar">
        <button type="button" className="btn sv-back" onClick={() => controller.closeSchematics()} title="Back to the map · Esc"><ChevronLeft size={16} aria-hidden="true" /><span className="sv-back-name">{world}</span></button>
        <span className="sv-crumb" aria-hidden="true">/</span>
        <span className="sv-heading"><Boxes size={16} aria-hidden="true" />Schematics</span>
        <span className="grow" />
        <label className="sv-search">
          <Search size={16} aria-hidden="true" />
          <input ref={search} placeholder="Search schematics…" aria-label="Search schematics" value={q} onChange={(e) => setQ(e.target.value)} />
          {q ? <button type="button" className="sv-search-clear" onClick={() => setQ("")} aria-label="Clear search"><X size={14} aria-hidden="true" /></button> : <kbd>/</kbd>}
        </label>
        <button type="button" className="btn btn-primary sv-import" onClick={pick} disabled={importing}>
          {importing ? <LoaderCircle className="spin" size={16} aria-hidden="true" /> : <Download size={16} aria-hidden="true" />}{importing ? "Reading…" : "Import"}
        </button>
        <button type="button" className="btn btn-icon sv-close" onClick={() => controller.closeSchematics()} aria-label="Close schematics" title="Close · Esc"><X size={18} aria-hidden="true" /></button>
        <input ref={fileInput} type="file" accept={ACCEPT} multiple hidden onChange={(e) => { const f = [...(e.target.files ?? [])]; e.target.value = ""; void importFiles(f); }} />
      </header>

      <div className="sv-card">
        <aside className="sv-lib" aria-label="Library">
          <div className="sv-lib-head">
            <span className="sv-lib-title">Library</span>
            <span className="caption">This computer, all worlds</span>
          </div>
          <nav className="sv-rail">
            {railItem("all", <LayoutGrid size={16} aria-hidden="true" />, "All schematics", counts.all)}
            {railItem("placed", <MapPin size={16} aria-hidden="true" />, `Placed in ${world}`, counts.placed)}
            {railItem("missing", <CircleAlert size={16} aria-hidden="true" />, "Not on this computer", counts.missing, true)}
            {railItem("recent", <Clock size={16} aria-hidden="true" />, "Recently imported", counts.recent)}
          </nav>
          {types.length > 0 && <>
            <div className="overline sv-lib-cap">File type</div>
            <nav className="sv-rail">
              {types.map(([x, n]) => (
                <button key={x} type="button" className="sv-rail-item" aria-current={filter === x ? "true" : undefined} onClick={() => setFilter(x as Filter)}>
                  <span className="grow mono">{x}</span><span className="sv-count tabular">{n}</span>
                </button>
              ))}
            </nav>
          </>}
          <span className="grow" />
          <div
            className={`sv-drop${drag ? " over" : ""}`}
            role="button"
            tabIndex={0}
            onClick={pick}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pick(); } }}
          >
            <Download size={18} aria-hidden="true" />
            <span className="sv-drop-title">{importing ? "Reading the file…" : "Drop files here or on the map"}</span>
            <span className="mono sv-drop-types">.litematic .schem .schematic .nbt</span>
          </div>
          <p className="sv-note"><Lock size={14} aria-hidden="true" /><span>Files stay on this computer. A shared plan keeps only the link to the original post. <AcademyLink to="schematicsImport" about="importing schematics">Learn more</AcademyLink></span></p>
        </aside>

        {empty ? (
          <main className="sv-main sv-empty">
            <div className="sv-empty-card">
              <span className="sv-empty-icon"><Boxes size={26} aria-hidden="true" /></span>
              <h1 className="title-md">No schematics yet</h1>
              <p>Found a build on Planet Minecraft or minecraft-schematics.com? Download it there, import it here and place it on your map.</p>
              <button type="button" className="btn btn-lg btn-primary" onClick={pick}><Download size={16} aria-hidden="true" />Import a schematic</button>
              <span className="caption"><span className="mono">.litematic .schem .schematic .nbt</span> · or drop files here</span>
            </div>
          </main>
        ) : (
          <main className="sv-main">
            <StagePane
              entry={entry}
              missing={miss}
              turns={turns}
              mirror={mirror}
              top={top}
              onTop={setTop}
              onTurn={() => setTurns((v) => (v + 1) % 4)}
              onMirror={() => setMirror((v) => !v)}
              onStage={setStage}
              onRemove={(h) => void controller.deleteSchematic(h)}
            />
            <section className="sv-film" aria-label={filterName}>
              <div className="sv-film-head">
                <span className="sv-film-title">{filterName}</span>
                <span className="caption">{files.length || gone.length ? "Sorted by last used" : ""}</span>
              </div>
              <div className="sv-strip" role="listbox" aria-label="Schematics" aria-orientation="horizontal">
                {files.map((e) => {
                  const n = placedCount.get(e.hash) ?? 0;
                  const img = e.thumb ?? e.preview;
                  return (
                    <button key={e.hash} type="button" role="option" aria-selected={e.hash === sel} className="sv-film-item" onClick={() => select(e.hash)} title={e.name}>
                      <span className={`sv-pic${img ? "" : " none"}${e.thumb ? " render" : ""}`}>
                        {img ? <img src={img} alt="" draggable={false} /> : <Boxes size={22} aria-hidden="true" />}
                        <span className={`sv-ring${e.hash === sel ? " sel" : ""}`} aria-hidden="true" />
                      </span>
                      <span className="sv-film-name">{e.name}</span>
                      <span className="sv-film-sub">{n ? `Placed ${n}×` : "Not placed yet"}</span>
                    </button>
                  );
                })}
                {gone.map((m) => (
                  <button key={m.ref.hash} type="button" role="option" aria-selected={m.ref.hash === sel} className="sv-film-item missing" onClick={() => select(m.ref.hash)} title={`${m.name} · not on this computer`}>
                    <span className="sv-pic get">
                      <CircleAlert size={20} aria-hidden="true" /><span className="sv-get-label">Get it</span>
                      <span className={`sv-ring${m.ref.hash === sel ? " sel" : ""}`} aria-hidden="true" />
                    </span>
                    <span className="sv-film-name">{m.name}</span>
                    <span className="sv-film-sub">Not on this computer</span>
                  </button>
                ))}
                {!files.length && !gone.length && <p className="caption sv-film-none">{q ? `No schematic matches “${q.trim()}”` : filter === "missing" ? "Every schematic used in this world is on this computer." : "Nothing here yet."}</p>}
              </div>
            </section>
          </main>
        )}

        {!empty && (
          <aside className="sv-details" aria-label={entry ? `${entry.name} details` : miss ? `${miss.name} details` : "Details"}>
            {editing && entry && editing.hash === entry.hash
              ? <ImportForm key={editing.hash} e={editing} world={world} fresh={Date.now() - editing.addedAt < 120_000 && !editing.sourceUrl} />
              : entry ? <FileDetails e={entry} placed={placedCount.get(entry.hash) ?? 0} world={world} turns={turns} mirror={mirror} />
              : miss ? <MissingDetails m={miss} world={world} onImport={pick} />
              : <div className="sv-details-body"><p className="caption">Pick a schematic in the filmstrip.</p></div>}
          </aside>
        )}
      </div>
      {drag && <div className="sv-drop-veil" aria-hidden="true"><Download size={22} /><span>Drop to import</span></div>}
    </div>
  );
}
